/**
 * useAutoRefresh - calls a fetch function on mount, on tab re-focus,
 * and optionally on a periodic interval. Includes debouncing so we don't
 * hammer providers when the user rapidly switches tabs.
 *
 * Usage:
 *   useAutoRefresh(fetchLive, { intervalMs: 60_000, minGapMs: 30_000 });
 *
 * Behavior:
 *   - On mount: fetches immediately (unless minGapMs has not elapsed since
 *     a prior fetch - useful when the same data is shown on multiple pages).
 *   - On tab return (document.visibilityState becomes 'visible'): fetches
 *     if minGapMs has elapsed.
 *   - On interval (if `intervalMs` provided): fetches every interval while
 *     the tab is visible. Paused while tab is hidden.
 *   - Patch CP: on failed fetch, schedules a short retry chain with
 *     exponential backoff. Mobile PWAs often launch before the network
 *     stack is ready, so the mount fetch fires immediately and fails.
 *     Pre-CP, the hook then waited up to a full interval (30-60s)
 *     before retrying, so users saw "live error" until they manually
 *     refreshed.
 *   - Patch CP: listens for `window.online` events. When the device goes
 *     from offline (or limited connectivity, on mobile PWA wake) to
 *     online, immediately refetch bypassing minGapMs because the user
 *     explicitly wants fresh data after a disconnect.
 *   - Patch CW: retry budget bumped from 4 to 6 attempts (2/4/8/16/32/64s
 *     backoff, ~2 min total recovery window) since 30s wasn't enough on
 *     slow mobile cold starts. Also adds `pageshow` listener to catch
 *     iOS PWA wakes that visibilitychange sometimes misses.
 *
 * The fetch function is captured by ref so the effect doesn't re-fire if
 * the caller passes a fresh closure each render.
 *
 * Failure detection: relies on the caller's fetchFn rejecting on error.
 * If a caller swallows its own errors and resolves successfully, this
 * hook can't see the failure - retry-on-failure is a no-op for that
 * caller, but the online-event and visibility-change listeners still
 * apply. The `void` on the call lets unhandled rejections surface; we
 * now actively catch the rejection to schedule a retry.
 */
import { useEffect, useRef } from 'react';

export interface AutoRefreshOptions {
  /** If set, fetch every N ms while the tab is visible. */
  intervalMs?: number;
  /** Don't fetch again unless this much time has passed since the last fetch. Defaults to 15s. */
  minGapMs?: number;
  /** If false, skip auto-fetching entirely. Useful when the feature is disabled. */
  enabled?: boolean;
  /** If true, don't fire on mount. The caller is expected to fetch on mount via its own effect. */
  skipInitialFetch?: boolean;
}

export function useAutoRefresh(
  fetchFn: () => void | Promise<void>,
  options: AutoRefreshOptions = {},
): void {
  const { intervalMs, minGapMs = 15_000, enabled = true, skipInitialFetch = false } = options;

  // Capture latest fetch fn in a ref so the effect doesn't tear down on re-render.
  const fetchRef = useRef(fetchFn);
  useEffect(() => {
    fetchRef.current = fetchFn;
  }, [fetchFn]);

  const lastFetchRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // Patch CP: retry state for cold-start failures. Reset on success and
    // on every online event so transient outages don't permanently exhaust
    // the budget.
    // Patch CW: bumped MAX_RETRIES from 4 to 6. Mobile cold starts on
    // slow networks (LTE handoff, weak signal, captive portal not yet
    // dismissed) sometimes take >30s for the network stack to be usable.
    // 6 retries with exponential backoff = 2 + 4 + 8 + 16 + 32 + 64 =
    // 126s total recovery window before falling back to interval polling.
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryAttempt = 0;
    const MAX_RETRIES = 6;

    const cancelRetry = (): void => {
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
    };

    const tryFetch = (bypassThrottle = false): void => {
      const now = Date.now();
      if (!bypassThrottle && now - lastFetchRef.current < minGapMs) return;
      lastFetchRef.current = now;
      try {
        const result = fetchRef.current();
        if (result && typeof (result as Promise<void>).then === 'function') {
          (result as Promise<void>)
            .then(() => {
              // Success: clear any pending retry chain and reset the
              // attempt counter so future cold starts get a fresh budget.
              cancelRetry();
              retryAttempt = 0;
            })
            .catch(() => {
              scheduleRetry();
            });
        }
      } catch {
        // Synchronous throw from fetchFn (rare but possible).
        scheduleRetry();
      }
    };

    const scheduleRetry = (): void => {
      if (retryAttempt >= MAX_RETRIES) {
        // Patch DP: after the exponential-backoff budget exhausts,
        // schedule a long-retry every 5 minutes. Catches the case
        // where the user is sitting on the PWA in foreground with a
        // flaky network: visibility never changes (PWA stays
        // visible), online never fires (network state never fully
        // toggles), pageshow never fires (no bfcache restore), so
        // none of the existing wake listeners can recover them.
        //
        // This timer is subject to the same iOS suspension as the
        // shorter retries - if the PWA is backgrounded, the OS may
        // drop it. That's fine: the wake-event listeners
        // (visibilitychange, focus, online, pageshow) handle the
        // resume-from-background case, and this handles the
        // foreground-stuck case. Defense in depth across the two
        // distinct stuck-state failure modes.
        cancelRetry();
        retryTimer = setTimeout(() => {
          retryTimer = undefined;
          retryAttempt = 0;
          if (document.visibilityState === 'visible') tryFetch(true);
        }, 5 * 60 * 1000);
        return;
      }
      const delay = 2000 * Math.pow(2, retryAttempt);
      retryAttempt += 1;
      cancelRetry();
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        // Only retry while visible; if the tab is hidden, visibility
        // change will trigger when it comes back.
        if (document.visibilityState === 'visible') tryFetch(true);
      }, delay);
    };

    // Initial fetch on mount (unless caller opts out).
    if (!skipInitialFetch) {
      tryFetch();
    } else {
      // Mark "now" as the last fetch so the focus handler debounces correctly.
      lastFetchRef.current = Date.now();
    }

    // On tab return.
    //
    // Patch DN: now matches onOnline + onPageshow semantics: reset the
    // retry budget and bypass the minGap throttle. Pre-DN this listener
    // just called tryFetch() with both safeguards intact, which meant
    // that if the retry chain had exhausted while the PWA was suspended
    // (six attempts to ~2 minutes total backoff is easy to hit on a
    // multi-minute background-suspension), nothing this listener did
    // could actually fire a fetch. The user would see 'live error'
    // until they manually reloaded the PWA. visibilitychange is the
    // common iOS wake signal (more than online or bfcache-pageshow),
    // so it has to behave the same way those handlers do.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        retryAttempt = 0;
        tryFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Patch CP: refresh when network connectivity is restored. The mobile
    // PWA cold-start failure mode and the cellular-reconnect case both
    // route through this listener. Bypasses minGapMs since the network
    // state change is a strong "user wants fresh data" signal.
    const onOnline = (): void => {
      retryAttempt = 0;
      tryFetch(true);
    };
    window.addEventListener('online', onOnline);

    // Patch CW: pageshow with persisted=true fires when the page is
    // restored from bfcache - the iOS PWA wake case. visibilitychange
    // is sometimes missed on iOS Safari PWA resume (known WebKit bug
    // since iOS 14ish), so listening for pageshow as a separate signal
    // catches wakes that visibility misses. Initial loads also fire
    // pageshow with persisted=false; we skip those because the mount
    // tryFetch above already handles initial load.
    const onPageshow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        retryAttempt = 0;
        tryFetch(true);
      }
    };
    window.addEventListener('pageshow', onPageshow);

    // Patch DP: window focus as an additional wake signal. There are
    // iOS scenarios where focus fires but visibilitychange does not:
    // split-view multitasking on iPad (switching keyboard focus
    // between two split apps without either becoming "hidden"),
    // picture-in-picture interactions, and some fast app-switcher
    // returns where the PWA was already considered "visible" during
    // the brief background period. Adding focus closes those gaps.
    // Same recovery semantics as visibility / online / pageshow:
    // reset the retry budget, bypass the minGap throttle.
    const onFocus = (): void => {
      retryAttempt = 0;
      tryFetch(true);
    };
    window.addEventListener('focus', onFocus);

    // Optional periodic refresh.
    let intervalId: ReturnType<typeof setInterval> | undefined;
    if (intervalMs && intervalMs > 0) {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') tryFetch();
      }, intervalMs);
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageshow);
      window.removeEventListener('focus', onFocus);
      cancelRetry();
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [enabled, intervalMs, minGapMs, skipInitialFetch]);
}
